import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkIcon from "@mui/icons-material/Work";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeIcon from "@mui/icons-material/Home";
import PublicIcon from "@mui/icons-material/Public";
import SchoolIcon from "@mui/icons-material/School";
import TranslateIcon from "@mui/icons-material/Translate";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { cvAdminApi } from "./api/cvAdminApi";
import {
  certificateFileName,
  downloadCertificateFile,
  hasCertificateFile,
  openCertificateFile,
} from "./api/cvCertificateFiles";
import { fmtDate, initials, safeMsg } from "./utils/cvUtils";
import StatusChip from "./components/StatusChip";

/* -------------------------------------------------------------------------- */
/* Small presentational helpers                                               */
/* -------------------------------------------------------------------------- */

function SectionCard({ title, icon, action, children }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        bgcolor: theme.palette.background.paper,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {icon ? (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function EmptyHint({ children = "Nothing here yet." }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
      {children}
    </Typography>
  );
}

function DetailRow({ icon, label, value }) {
  const theme = useTheme();
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      {icon ? (
        <Box sx={{ color: alpha(theme.palette.text.secondary, 0.9), mt: "2px" }}>
          {icon}
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
          {value ?? "-"}
        </Typography>
      </Box>
    </Stack>
  );
}

function MetaRow({ label, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>{children}</Box>
    </Stack>
  );
}

const PROFICIENCY_COLOR = {
  basic: "default",
  intermediate: "info",
  advanced: "primary",
  fluent: "success",
  native: "secondary",
};

/* -------------------------------------------------------------------------- */
/* Main page                                                                  */
/* -------------------------------------------------------------------------- */

export default function CvShowPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [certBusy, setCertBusy] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  // dialogs
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenDays, setReopenDays] = useState(7);
  const [reopenNote, setReopenNote] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const toast = useCallback((msg, severity = "success") => {
    setSnack({ open: true, msg, severity });
  }, []);

  const loadVersions = useCallback(async () => {
    if (!userId) return;
    setVersionsLoading(true);
    try {
      const res = await cvAdminApi.versions(userId);
      setVersions(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, [userId]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const res = await cvAdminApi.getSubmission(userId);
      setData(res?.data || {});
    } catch (err) {
      setError(safeMsg(err, "Failed to load CV submission."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
    loadVersions();
  }, [load, loadVersions]);

  const user = data?.user || {};
  const profile = data?.profile || {};
  const experiences = Array.isArray(data?.experiences) ? data.experiences : [];
  const educations = Array.isArray(data?.educations) ? data.educations : [];
  const languages = Array.isArray(data?.languages) ? data.languages : [];
  const certificates = Array.isArray(data?.certificates) ? data.certificates : [];

  const displayName = profile?.full_name || user?.name || "Unknown employee";
  const aboutText = profile?.about_me || profile?.summary || "";

  const skillsList = useMemo(() => {
    const raw = profile?.skills;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw)
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [profile?.skills]);

  const windowOpen = Boolean(user?.window_open);

  /* ----------------------------- actions ------------------------------ */

  const runAction = useCallback(
    async (fn, successMsg) => {
      setBusy(true);
      try {
        await fn();
        toast(successMsg, "success");
        await load();
        await loadVersions();
      } catch (err) {
        toast(safeMsg(err, "Action failed."), "error");
      } finally {
        setBusy(false);
      }
    },
    [load, loadVersions, toast]
  );

  const handleCertificateFile = useCallback(
    async (cert, mode) => {
      const key = `${cert?.id || ""}:${mode}`;
      setCertBusy(key);
      try {
        if (mode === "download") await downloadCertificateFile(cert);
        else await openCertificateFile(cert);
      } catch (err) {
        toast(safeMsg(err, "Could not open the attachment."), "error");
      } finally {
        setCertBusy("");
      }
    },
    [toast]
  );

  const handleApprove = () =>
    runAction(() => cvAdminApi.approve(userId), "Submission approved.");

  const handleReject = async () => {
    setRejectOpen(false);
    const reason = rejectReason.trim();
    await runAction(
      () => cvAdminApi.reject(userId, reason),
      "Submission rejected."
    );
    setRejectReason("");
  };

  const handleReopen = async () => {
    setReopenOpen(false);
    const days = Number(reopenDays) || 7;
    const note = reopenNote.trim() || null;
    await runAction(
      () => cvAdminApi.reopen(userId, days, note),
      "Editing window reopened."
    );
    setReopenNote("");
    setReopenDays(7);
  };

  const handleCloseWindow = () =>
    runAction(() => cvAdminApi.closeWindow(userId), "Editing window closed.");

  const handleDelete = async () => {
    setDeleteOpen(false);
    setBusy(true);
    try {
      await cvAdminApi.deleteSubmission(userId);
      toast("Submission deleted.", "success");
      navigate("/cv/review");
    } catch (err) {
      toast(safeMsg(err, "Delete failed."), "error");
      setBusy(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await cvAdminApi.generate(userId, { format: "pdf" });
      toast("CV generated.", "success");
      await loadVersions();
    } catch (err) {
      toast(safeMsg(err, "Generation failed."), "error");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (version) => {
    const vid = version?.id;
    if (!vid) return;
    try {
      const res = await cvAdminApi.downloadVersion(vid);
      const blob = res?.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ext = String(version?.format || "pdf").toLowerCase();
      a.download =
        version?.file_name ||
        `cv_${user?.employee_code || userId}_v${version?.version || vid}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast(safeMsg(err, "Download failed."), "error");
    }
  };

  /* ----------------------------- render ------------------------------- */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          p: 4,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading CV submission…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: "auto" }}>
      {/* Top bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/cv/review")}
          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
        >
          Back to list
        </Button>

        <Stack direction="row" spacing={1.5}>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => {
                  load();
                  loadVersions();
                }}
                disabled={busy}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/cv/review/${userId}/edit`)}
            sx={{ fontWeight: 800, borderRadius: 2 }}
          >
            Edit
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }}>
          {error}
        </Alert>
      ) : null}

      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.5, md: 4 },
          mb: 3,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.14
          )} 0%, ${alpha(theme.palette.primary.light, 0.06)} 55%, ${alpha(
            theme.palette.background.paper,
            0.2
          )} 100%)`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -60,
            right: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: alpha(theme.palette.primary.main, 0.1),
            filter: "blur(8px)",
            pointerEvents: "none",
          }}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 3 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ position: "relative" }}
        >
          <Avatar
            sx={{
              width: { xs: 64, md: 84 },
              height: { xs: 64, md: 84 },
              fontSize: { xs: 24, md: 30 },
              fontWeight: 900,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            {initials(displayName)}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 900, lineHeight: 1.15 }}
              >
                {displayName}
              </Typography>
              <StatusChip row={{ status: profile?.status, submitted_at: profile?.submitted_at }} />
            </Stack>

            {profile?.current_job_title ? (
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 700, mt: 0.25 }}
              >
                {profile.current_job_title}
              </Typography>
            ) : null}

            <Stack
              direction="row"
              spacing={2.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1.25 }}
            >
              {user?.email || profile?.email ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {user?.email || profile?.email}
                  </Typography>
                </Stack>
              ) : null}
              {user?.employee_code ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <BadgeIcon fontSize="small" color="action" />
                  <Typography variant="body2">{user.employee_code}</Typography>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Action bar */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 3,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleApprove}
            disabled={busy}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setRejectOpen(true)}
            disabled={busy}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Reject
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LockOpenIcon />}
            onClick={() => setReopenOpen(true)}
            disabled={busy}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Reopen
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LockIcon />}
            onClick={handleCloseWindow}
            disabled={busy || !windowOpen}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Close window
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="text"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteOpen(true)}
            disabled={busy}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* LEFT: content */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={3}>
            {/* About */}
            <SectionCard title="About" icon={<PersonIcon fontSize="small" />}>
              {aboutText ? (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}
                >
                  {aboutText}
                </Typography>
              ) : (
                <EmptyHint>No summary provided.</EmptyHint>
              )}
            </SectionCard>

            {/* Skills */}
            <SectionCard title="Skills" icon={<StarIcon fontSize="small" />}>
              {skillsList.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {skillsList.map((s, i) => (
                    <Chip
                      key={`${s}-${i}`}
                      label={s}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <EmptyHint>No skills listed.</EmptyHint>
              )}
            </SectionCard>

            {/* Personal details */}
            <SectionCard title="Personal details" icon={<BadgeIcon fontSize="small" />}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 2.5,
                }}
              >
                <DetailRow
                  icon={<PhoneIcon fontSize="small" />}
                  label="Phone"
                  value={profile?.phone}
                />
                <DetailRow
                  icon={<HomeIcon fontSize="small" />}
                  label="Address"
                  value={profile?.address}
                />
                <DetailRow
                  icon={<PublicIcon fontSize="small" />}
                  label="Nationality"
                  value={profile?.nationality}
                />
                <DetailRow
                  icon={<WorkIcon fontSize="small" />}
                  label="Years of experience"
                  value={
                    profile?.years_experience != null &&
                    profile?.years_experience !== ""
                      ? profile.years_experience
                      : "-"
                  }
                />
                <DetailRow
                  label="Open to relocate"
                  value={
                    profile?.relocate == null
                      ? "-"
                      : profile.relocate
                      ? "Yes"
                      : "No"
                  }
                />
                <DetailRow
                  label="Joining availability"
                  value={
                    profile?.joining_availability_date
                      ? fmtDate(profile.joining_availability_date)
                      : "-"
                  }
                />
                <DetailRow
                  label="Expected salary"
                  value={profile?.expected_salary}
                />
                <DetailRow
                  label="Current salary"
                  value={profile?.current_salary}
                />
              </Box>
            </SectionCard>

            {/* Experience */}
            <SectionCard title="Experience" icon={<WorkIcon fontSize="small" />}>
              {experiences.length ? (
                <Stack spacing={2}>
                  {experiences.map((exp, i) => (
                    <Box key={exp?.id ?? i}>
                      {i > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {exp?.position || "Role"}
                        {exp?.company ? (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            {"  @ "}
                            {exp.company}
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(exp?.start_date)} – {fmtDate(exp?.end_date)}
                      </Typography>
                      {exp?.description ? (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, whiteSpace: "pre-line" }}
                        >
                          {exp.description}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyHint>No experience entries.</EmptyHint>
              )}
            </SectionCard>

            {/* Education */}
            <SectionCard title="Education" icon={<SchoolIcon fontSize="small" />}>
              {educations.length ? (
                <Stack spacing={2}>
                  {educations.map((ed, i) => (
                    <Box key={ed?.id ?? i}>
                      {i > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {ed?.degree || "Degree"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ed?.institution || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(ed?.graduation_date)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyHint>No education entries.</EmptyHint>
              )}
            </SectionCard>

            {/* Languages */}
            <SectionCard title="Languages" icon={<TranslateIcon fontSize="small" />}>
              {languages.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {languages.map((lang, i) => {
                    const prof = String(lang?.proficiency || "").toLowerCase();
                    return (
                      <Chip
                        key={lang?.id ?? i}
                        label={
                          lang?.proficiency
                            ? `${lang?.language || "-"} — ${lang.proficiency}`
                            : lang?.language || "-"
                        }
                        size="small"
                        color={PROFICIENCY_COLOR[prof] || "default"}
                        variant="outlined"
                        sx={{ fontWeight: 700, borderRadius: 2 }}
                      />
                    );
                  })}
                </Stack>
              ) : (
                <EmptyHint>No languages listed.</EmptyHint>
              )}
            </SectionCard>

            {/* Certificates */}
            <SectionCard
              title="Certificates"
              icon={<WorkspacePremiumIcon fontSize="small" />}
            >
              {certificates.length ? (
                <Stack spacing={2}>
                  {certificates.map((c, i) => (
                    <Box key={c?.id ?? i}>
                      {i > 0 ? <Divider sx={{ mb: 2 }} /> : null}
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {c?.title || "Certificate"}
                        {c?.issuer ? (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            {"  — "}
                            {c.issuer}
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {fmtDate(c?.issue_date)} → {fmtDate(c?.expiry_date)}
                      </Typography>

                      {hasCertificateFile(c) ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}
                        >
                          <Chip
                            icon={<AttachFileIcon />}
                            label={certificateFileName(c) || "Attachment"}
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: 260, fontWeight: 700 }}
                          />
                          <Button
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => handleCertificateFile(c, "open")}
                            disabled={certBusy === `${c?.id || ""}:open`}
                          >
                            Open
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleCertificateFile(c, "download")}
                            disabled={certBusy === `${c?.id || ""}:download`}
                          >
                            Download
                          </Button>
                        </Stack>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          No attachment uploaded.
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <EmptyHint>No certificates.</EmptyHint>
              )}
            </SectionCard>
          </Stack>
        </Box>

        {/* RIGHT: meta + versions */}
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={3} sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            {/* Meta */}
            <SectionCard title="Review status" icon={<PersonIcon fontSize="small" />}>
              <Stack spacing={1.75}>
                <MetaRow label="Status">
                  <StatusChip
                    row={{
                      status: profile?.status,
                      submitted_at: profile?.submitted_at,
                    }}
                  />
                </MetaRow>
                <Divider />
                <MetaRow label="Submitted">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fmtDate(profile?.submitted_at)}
                  </Typography>
                </MetaRow>
                <MetaRow label="Reviewed at">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fmtDate(profile?.reviewed_at)}
                  </Typography>
                </MetaRow>
                <Divider />
                <MetaRow label="Editable until">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtDate(user?.cv_editable_until)}
                    </Typography>
                  </Stack>
                </MetaRow>
                <MetaRow label="Window">
                  <Chip
                    size="small"
                    icon={windowOpen ? <LockOpenIcon /> : <LockIcon />}
                    label={windowOpen ? "Open" : "Closed"}
                    color={windowOpen ? "success" : "default"}
                    variant="outlined"
                    sx={{ fontWeight: 800, borderRadius: 2 }}
                  />
                </MetaRow>

                {profile?.review_reason ? (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Review reason
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, whiteSpace: "pre-line" }}
                      >
                        {profile.review_reason}
                      </Typography>
                    </Box>
                  </>
                ) : null}

                {profile?.reopen_reason ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Reopen reason
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, whiteSpace: "pre-line" }}
                    >
                      {profile.reopen_reason}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </SectionCard>

            {/* Generated versions */}
            <SectionCard
              title="Generated versions"
              icon={<PictureAsPdfIcon fontSize="small" />}
              action={
                <Button
                  size="small"
                  variant="contained"
                  startIcon={
                    generating ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  onClick={handleGenerate}
                  disabled={generating}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                >
                  Generate
                </Button>
              }
            >
              {versionsLoading ? (
                <Stack alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={22} />
                </Stack>
              ) : versions.length ? (
                <Stack spacing={1.25}>
                  {versions.map((v, i) => (
                    <Stack
                      key={v?.id ?? i}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, wordBreak: "break-word" }}
                        >
                          {v?.file_name ||
                            `Version ${v?.version ?? v?.id ?? i + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            v?.format
                              ? String(v.format).toUpperCase()
                              : null,
                            fmtDate(v?.created_at || v?.generated_at),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(v)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <EmptyHint>No generated versions yet.</EmptyHint>
              )}
            </SectionCard>
          </Stack>
        </Box>
      </Box>

      {/* Reject dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reject submission</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Provide a reason for rejecting this CV. The employee may see this.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={busy}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reopen editing window</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Allow the employee to edit their CV again for a number of days.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              type="number"
              label="Days"
              value={reopenDays}
              onChange={(e) => setReopenDays(e.target.value)}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Note (optional)"
              value={reopenNote}
              onChange={(e) => setReopenNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleReopen}
            disabled={busy}
          >
            Reopen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Delete submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete this CV submission. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={busy}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
